import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import ErrorToast from './ErrorToast';
import { UserService } from '../services/UserService';
import Loader from './Loader';
import { useNavigate } from 'react-router-dom';

const DeleteAccountModal = ({ targetId }: { targetId: string }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [hasPendingOrders, setHasPendingOrders] = useState(false);

  const deleteAccountMutation = useMutation(
    () => {
      return UserService.deleteAccount();
    },
    {
      onSuccess: () => {
        navigate('/logout');
      },
      onError: (e: any) => {
        if (e?.response?.status === 409) {
          setHasPendingOrders(true);
        } else {
          ErrorToast(t('Errors.general'));
        }
      },
    },
  );

  useEffect(() => {
    // @ts-ignore
    if (typeof window?.UIkit !== 'undefined') {
      // @ts-ignore
      UIkit.util.on(`#${targetId}`, 'hidden', function () {
        setHasPendingOrders(false);
      });
    }
  }, []);

  return (
    <div id="delete-account-modal-wrapper">
      <div
        className="hidden lg:p-20 uk- open"
        id={targetId}
        data-uk-modal="container: #delete-account-modal-wrapper;"
      >
        {deleteAccountMutation.isLoading && <Loader />}

        <div className="uk-modal-dialog tt relative overflow-hidden mx-auto bg-white shadow-xl rounded-lg md:w-[520px] w-full dark:bg-dark2 text-black dark:text-white">
          <div className="text-center py-4 border-b mb-0 dark:border-slate-700">
            <div className="text-base font-semibold text-black dark:text-white px-10">
              Delete Account
            </div>
            <button
              type="button"
              className="button-icon absolute top-0 right-0 m-2.5 uk-modal-close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {hasPendingOrders ? (
            <>
              <div className="p-5 text-sm">
                You have back-ordered or pre-ordered games that have not yet
                been delivered.
                <br />
                To delete your account, please either wait until all your orders
                have been fulfilled, or cancel and refund any outstanding
                orders.
                <br />
                This ensures you don’t lose access to any purchased content.
              </div>
            </>
          ) : (
            <>
              <div className="p-5 text-sm">
                Deleting your account is <b>permanent</b> and{' '}
                <b>cannot be undone</b>.
                <br />
                Before proceeding, please ensure you have{' '}
                <b>saved all your game keys</b> and any other important
                information. Once you confirm deletion:
                <br />
                <br />
                <ul className="list-disc list-outside pl-5">
                  <li>You will be immediately locked out of your account.</li>
                  <li>All associated data will be permanently removed.</li>
                  <li>
                    A confirmation email will be sent once your account is
                    successfully deleted. This email will also include a list of
                    your game keys for your reference.
                  </li>
                  <li>
                    Any pre-order or back-order keys you purchased will be
                    separately emailed to you once they are available.
                  </li>
                </ul>
                <br />
                Please double-check everything before continuing. This action is{' '}
                <b>irreversible</b>.
              </div>

              <div className="px-5 pb-5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="button py-2 px-12 text-[14px] bg-slate-900 dark:bg-slate-100 text-slate-50 dark:text-slate-800"
                    onClick={() => {
                      void deleteAccountMutation.mutate();
                    }}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="button py-2 px-12 text-[14px] border dark:border-gray-600/60 dark:text-slate-50 text-slate-800 uk-modal-close"
                  >
                    No
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
